import React from "react";

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary that catches lazy-loaded chunk loading failures
 * (e.g., when Cloudflare Workers serves a stale deployment and
 * the dynamically imported JS chunk returns 404).
 *
 * Instead of showing a blank white page, this displays a clear
 * error message with a reload button.
 */
export class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ChunkErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[ChunkErrorBoundary] Lazy-load or render failure:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes("Failed to fetch dynamically imported module") ||
        this.state.error?.message?.includes("Loading chunk") ||
        this.state.error?.message?.includes("Loading CSS chunk") ||
        this.state.error?.name === "ChunkLoadError";

      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="max-w-md w-full bg-slate-900 border border-amber-500/40 rounded-3xl p-8 shadow-2xl space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-950/80 border border-amber-500/40 flex items-center justify-center mx-auto text-amber-400 font-bold text-2xl">
              ⟳
            </div>
            <h1 className="text-xl font-extrabold text-white">
              {isChunkError ? "New Version Available" : "Page Load Error"}
            </h1>
            <p className="text-xs text-slate-400 leading-relaxed">
              {isChunkError
                ? "A newer version of Corvenio has been deployed. Please reload the page to load the latest version."
                : "An error occurred while loading this page. This may be caused by a network issue or a temporary deployment update."}
            </p>
            {this.state.error && (
              <pre className="text-[11px] font-mono bg-slate-950 p-3 rounded-xl border border-slate-800 text-amber-300 text-left overflow-x-auto max-h-24">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-indigo-600/30 transition-all"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
